import * as ViewerConstants from 'components/viewer/viewer-constants';
import {VIDEO_SPEEDS} from 'constants/video-speed-constants';
import * as Immutable from 'immutable';
import noop from 'lodash/noop';
import {createReducer} from 'redux-store';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {LAYOUTS, LayoutUtils} from 'utils/layout-utils';
import {SETTINGS_MENU_STATES} from 'utils/settings-menu-utils';

import type {List, Map, MapOf, OrderedMap} from 'immutable';
import type {ActionsMap} from 'redux-store';
import type {Subscription} from 'rxjs';

type VideoState = MapOf<{
    currentTime: number;
    duration: number;
    volume: number;
    muted: boolean;
    playheadPosition: number;
    expectedState: unknown;
    currentState: unknown;
    showInitialPlayButton: boolean;
    isPlaying: boolean;
}>;

export type VideoActions = MapOf<{
    toggle: (data: unknown) => unknown;
    play: (data: unknown) => unknown;
    pause: (data: unknown) => unknown;
    setCurrentPosition: () => unknown;
    setVolume: (data: unknown) => unknown;
    setMute: (data: unknown) => unknown;
    setStreamVisibility: (data: unknown) => unknown;
    showPtzMenu: (data: unknown) => unknown;
    seek: (data: unknown) => unknown;
    setSpeed: (data: unknown) => unknown;
}>;

export type ViewerStore = {
    rxVideoState: ReplaySubject<VideoState>;
    videoActions: VideoActions;
    videoOrder: List<unknown>;
    settingsMenuState: string;
    videoSpeed: string;
    videoBackgroundColor: string;

    simGraphDisplay: boolean;
    showPtzMenu: boolean;
    videoLayout: string;
    offFeeds: Map<string, boolean>;

    timeChangeSubscribers: OrderedMap<string, Subscription>;
    directStreamInfo: MapOf<{
        captureNodeId: string;
        hostNames: string;
        directStreamToken: string;
    }>;
};

const initialState = (): ViewerStore => {
    return {
        rxVideoState: new ReplaySubject(1),
        videoActions: Immutable.Map({
            toggle: noop,
            play: noop,
            pause: noop,
            setCurrentPosition: noop,
            setVolume: noop,
            setMute: noop,
            setStreamVisibility: noop,
            showPtzMenu: noop,
            seek: noop,
            setSpeed: noop,
        }),
        videoOrder: Immutable.List(),
        settingsMenuState: SETTINGS_MENU_STATES.NONE,
        videoSpeed: VIDEO_SPEEDS.NORMAL,
        videoBackgroundColor: ViewerConstants.VIDEO_BACKGROUND_COLOR,

        simGraphDisplay: false,
        showPtzMenu: false,
        videoLayout: LAYOUTS.HOR,
        offFeeds: Immutable.Map(),

        timeChangeSubscribers: Immutable.OrderedMap<string, Subscription>(),
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type
        directStreamInfo: Immutable.Map(),
    };
};

const actionsMap: ActionsMap<ViewerStore> = {
    [ViewerConstants.SUBSCRIBE_VIDEO_STATE_CHANGE]: (state, action) => {
        if (!state.timeChangeSubscribers.has(action.id)) {
            const instance = state.rxVideoState.subscribe(action.fn);
            return {
                timeChangeSubscribers: state.timeChangeSubscribers.set(action.id, instance),
            };
        }
        return {};
    },
    [ViewerConstants.UN_SUBSCRIBE_VIDEO_STATE_CHANGE]: (state, action) => {
        if (state.timeChangeSubscribers.has(action.id)) {
            state.timeChangeSubscribers.get(action.id).unsubscribe();
            return {
                timeChangeSubscribers: state.timeChangeSubscribers.remove(action.id),
            };
        }
        return {};
    },
    [ViewerConstants.DISPOSE_VIEWER_STORE]: (state) => {
        state.rxVideoState.unsubscribe();
        return initialState();
    },
    [ViewerConstants.SEEK_VIDEO]: (state, action) => {
        state.videoActions.get('seek')(action.data);
        return {};
    },
    [ViewerConstants.SET_VISIBLE_STREAMS]: (state, action) => {
        state.videoActions.get('setStreamVisibility')(action.data);
        return {};
    },
    [ViewerConstants.TOGGLE_VIDEO]: (state, action) => {
        state.videoActions.get('toggle')(action.data);
        return {};
    },
    [ViewerConstants.SET_VOLUME]: (state, action) => {
        const {volume} = action;
        state.videoActions.get('setVolume')(volume);
        return {};
    },
    [ViewerConstants.SET_MUTE]: (state, action) => {
        state.videoActions.get('setMute')(action.data);
        return {};
    },
    [ViewerConstants.PLAY_VIDEO]: (state, action) => {
        state.videoActions.get('play')(action.data);
        return {};
    },
    [ViewerConstants.PAUSE_VIDEO]: (state, action) => {
        state.videoActions.get('pause')(action.data);
        return {};
    },
    [ViewerConstants.SET_VIDEO_SPEED]: (state, action) => {
        state.videoActions.get('setSpeed')(action.speed);
        return {
            videoSpeed: action.speed,
        };
    },
    [ViewerConstants.VIDEO_INIT]: (state, action) => {
        return {
            videoActions: action.data,
        };
    },
    [ViewerConstants.SET_SETTINGS_MENU_STATE]: (state, action) => {
        return {
            settingsMenuState: action.state,
        };
    },
    [ViewerConstants.UPDATE_VIDEO_ORDER]: (state, action) => {
        return {
            videoOrder: action.data,
        };
    },
    [ViewerConstants.TOGGLE_SIM_GRAPH]: (state, action) => {
        return {
            simGraphDisplay: action.simGraphDisplay,
        };
    },
    [ViewerConstants.TOGGLE_PTZ_MENU]: (state) => {
        state.videoActions.get('showPtzMenu')(!state.showPtzMenu);
        return {
            showPtzMenu: !state.showPtzMenu,
        };
    },
    [ViewerConstants.UPDATE_OFF_FEEDS]: (state, action) => {
        const updatedOffFeeds = action.offFeeds;
        let newVideoLayout = state.videoLayout;
        if (!LayoutUtils.availableLayouts(action.totalSize - updatedOffFeeds.size).includes(state.videoLayout)) {
            newVideoLayout = LAYOUTS.HOR;
        }

        return {
            offFeeds: updatedOffFeeds,
            videoLayout: newVideoLayout,
        };
    },
    [ViewerConstants.SET_LAYOUT]: (state, action) => {
        return {
            videoLayout: action.data,
        };
    },
    [ViewerConstants.SET_DIRECT_STREAM_INFO]: (state, action) => {
        return {
            directStreamInfo: action.directStreamInfo,
        };
    },
};

export default createReducer<ViewerStore>(initialState(), actionsMap);
